import React from "react";
import PersonIcon from "@mui/icons-material/Person";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import {
  calculateAge,
  formatAddressIfPresent,
  formatCountry,
  formatDate,
  hasDisplayValue,
} from "../patient/utils/patientUtils";
import type { SummaryTheme } from "../patient/components/patientSummaryUi";
import type { PatientTokenInfo } from "../patient/components/ModalHeader";
import { formatTokenTime } from "../patient/components/ModalHeader";
import {
  PATIENT_TYPE_STYLES,
  formatPatientTypeLabel,
  resolvePatientTypeLabelFromPatientAndTreatments,
} from "../appointment/viewappointment/components/appointmentViewShared";

interface TreatmentPatientHeaderProps {
  patientData: any;
  theme?: SummaryTheme;
  onEdit?: (id: string, e: React.MouseEvent) => void;
  tokenInfo?: PatientTokenInfo | null;
  examinationData?: any[];
  /** Optional extra buttons rendered alongside Edit */
  extraActions?: React.ReactNode;
}

function labelClass(theme: SummaryTheme): string {
  const isDark = theme === "dark";
  return `whitespace-nowrap text-[11px] font-bold uppercase tracking-wider ${
    isDark ? "text-white" : "text-black"
  }`;
}

function valueClass(theme: SummaryTheme, accent = false): string {
  const isDark = theme === "dark";
  if (accent) return isDark ? "text-red-300" : "text-red-600";
  return isDark ? "text-white" : "text-black";
}

const TreatmentPatientHeader: React.FC<TreatmentPatientHeaderProps> = ({
  patientData,
  theme = "light",
  onEdit,
  tokenInfo,
  examinationData = [],
  extraActions,
}) => {
  const isDark = theme === "dark";
  const fullName = `${patientData?.title || ""} ${patientData?.fullName || "Unknown"}`.trim();
  const age = patientData?.dob ? calculateAge(patientData.dob) : null;
  const tokenNo =
    tokenInfo?.tokenNo != null && String(tokenInfo.tokenNo).trim() !== ""
      ? String(tokenInfo.tokenNo)
      : "";
  const tokenTime = formatTokenTime(tokenInfo?.queueGeneratedAt);
  const showToken = Boolean(tokenNo) && Number(tokenNo) > 0;
  const patientTypeLabel = resolvePatientTypeLabelFromPatientAndTreatments(
    patientData,
    examinationData
  );
  const address = formatAddressIfPresent(patientData?.address);

  const displayOrNo = (value: unknown): string =>
    hasDisplayValue(value) ? String(value) : "No";

  const requiredDetails: Array<{ label: string; value: string; accent?: boolean }> = [
    { label: "Email", value: displayOrNo(patientData?.email) },
    { label: "Age", value: age !== null ? `${age} yrs` : "No" },
    { label: "Contact", value: displayOrNo(patientData?.mobileNo) },
    { label: "Gender", value: displayOrNo(patientData?.gender) },
  ];

  const clinicalDetails: Array<{ label: string; value: string }> = [
    { label: "X-Ray", value: displayOrNo(patientData?.xRayNo) },
    { label: "OPG", value: displayOrNo(patientData?.opgNo) },
    { label: "CBCT", value: displayOrNo(patientData?.cbctNo) },
  ];

  const optionalDetails: Array<{ label: string; value: string; accent?: boolean }> = [];
  const push = (label: string, value: unknown, accent = false) => {
    if (hasDisplayValue(value)) {
      optionalDetails.push({ label, value: String(value), accent });
    }
  };

  push("Reg No", patientData?.regNo);
  push("Blood", patientData?.bloodGroup, true);
  push("DOB", patientData?.dob ? formatDate(patientData.dob) : null);
  push("NIC", patientData?.nic);
  push("WhatsApp", patientData?.whatsapp);
  push("Alt. Phone", patientData?.smobileNo);
  push("Registered", patientData?.registerdDate ? formatDate(patientData.registerdDate) : null);
  push("Marital", patientData?.maritalStatus);
  push("Occupation", patientData?.occupation);
  push("Religion", patientData?.religion);
  push("Referred By", patientData?.nameOfIntroduce);
  push("Country", formatCountry(patientData));
  if (address) push("Address", address);

  const details = [...requiredDetails, ...optionalDetails];

  const actionBtn =
    "inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors";

  return (
    <div
      className={`${isDark ? "bg-slate-900/95" : "bg-white/95"}`}
    >
      <div className="mx-auto max-w-[1400px] px-4 py-2 sm:px-5 sm:py-2.5">
        <div
          className={`flex flex-col gap-2 lg:gap-3 ${showToken ? "lg:flex-row lg:items-stretch" : ""}`}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2.5 sm:gap-3">
            {patientData?.image ? (
              <img
                src={patientData.image}
                alt={fullName}
                className="h-10 w-10 shrink-0 rounded-full object-cover sm:h-12 sm:w-12"
              />
            ) : (
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
                  isDark ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-600"
                }`}
              >
                <PersonIcon sx={{ fontSize: 24 }} />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2
                      className={`truncate text-base font-bold tracking-tight sm:text-lg ${
                        isDark ? "text-white" : "text-black"
                      }`}
                    >
                      {fullName}
                    </h2>
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold border ${
                        PATIENT_TYPE_STYLES[patientTypeLabel] ||
                        PATIENT_TYPE_STYLES["Existing Patient"]
                      }`}
                    >
                      {formatPatientTypeLabel(patientTypeLabel)}
                    </span>
                    <span
                      className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        isDark
                          ? "bg-slate-800 text-slate-300"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {patientData?.patientType || "Normal"}
                    </span>
                  </div>
                </div>

                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                  {extraActions}

                  {onEdit && patientData?._id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(patientData._id, e);
                      }}
                      className={`${actionBtn} ${
                        isDark
                          ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      <BorderColorIcon sx={{ fontSize: 14 }} />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                  ) : null}
                </div>
              </div>

              {details.length > 0 || clinicalDetails.length > 0 ? (
                <div
                  className={`mt-2 flex flex-col gap-1 border-t pt-2 ${
                    isDark ? "border-slate-800" : "border-slate-100"
                  }`}
                >
                  {details.length > 0 ? (
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      {details.map((item, index) => (
                        <div
                          key={`${item.label}-${index}`}
                          className="flex items-baseline gap-1 whitespace-nowrap"
                        >
                          <span className={labelClass(theme)}>{item.label}:</span>
                          <span className={`text-sm font-medium ${valueClass(theme, item.accent)}`}>
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {clinicalDetails.length > 0 ? (
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <span className={labelClass(theme)}>Clinical:</span>
                      {clinicalDetails.map((item) => (
                        <div
                          key={item.label}
                          className="flex items-baseline gap-1 whitespace-nowrap"
                        >
                          <span className={labelClass(theme)}>{item.label}:</span>
                          <span className={`text-sm font-medium ${valueClass(theme)}`}>
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {showToken ? (
            <div
              className={`flex w-full shrink-0 items-center gap-2.5 lg:w-[20rem] xl:w-[22rem] lg:border-l lg:pl-3.5 ${
                isDark ? "border-slate-700" : "border-slate-200"
              }`}
              title={tokenTime ? `Token generated at ${tokenTime}` : "Token number"}
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600"
                }`}
              >
                <ConfirmationNumberOutlinedIcon sx={{ fontSize: 22 }} />
              </span>
              <div className="min-w-0">
                <p className={labelClass(theme)}>Token</p>
                <div
                  className={`flex items-baseline gap-2 leading-none ${
                    isDark ? "text-emerald-300" : "text-emerald-700"
                  }`}
                >
                  <span className="text-3xl font-bold tabular-nums tracking-tight sm:text-4xl">
                    {tokenNo}
                  </span>
                  {tokenTime ? (
                    <span className="text-sm font-medium opacity-80">{tokenTime}</span>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TreatmentPatientHeader;
